import { Button } from "antd";
import { useState } from "react";
import { FiChevronDown } from "react-icons/fi";
import { useHistory } from "react-router-dom";
import {
  useBrandPaymentHistory,
  useBrandPaymentHistoryDetail,
} from "../../hooks/usePayment";
import { formatCurrency, parseApiAmount } from "../../utils/amountFormat";
import {
  brandPaymentStatusClass,
  brandPaymentStatusLabel,
} from "../../utils/brandPaymentStatus";
import {
  influencerPaymentStatusClass,
  influencerPaymentStatusLabel,
} from "../../utils/influencerPaymentStatus";

const buildPaymentDetailState = (item) => ({
  campaignTitle: item.campaign_title,
  campaignCategory: item.campaign_category,
  campaignId: item.campaign_id,
  contentFormat: item.content_format,
  seedingType: item.seeding_type,
  paidAmount: item.paid_amount,
  paidAt: item.paid_at,
  merchantUid: item.merchant_uid,
  payerName: item.payer_name,
  cardBrand: item.card_brand,
  cardLast4: item.card_last4,
  payMethod: item.pay_method,
  status: item.status,
  currency: item.currency,
});

const SETTLEMENT_VISIBLE_STATUSES = [
  "paid",
  "pending",
  "failed",
  "cancelled",
  "brand_pending",
  "brand_cancelled",
  "brand_failed",
];

const canOpenSettlementDetail = (status) =>
  SETTLEMENT_VISIBLE_STATUSES.includes(status);

const CreatorPayoutList = ({ isPending, isError, creatorPayoutLines }) => {
  if (isPending || isError || !Array.isArray(creatorPayoutLines)) {
    return null;
  }

  return (
    <ul className="space-y-2">
      {creatorPayoutLines.map((row) => (
        <li
          key={String(row.id)}
          className="flex flex-col gap-2 rounded-xl border border-[#eef2f6] bg-white px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="min-w-0">
            <span
              className={`inline-flex rounded-[8px] px-2 py-0.5 text-[11px] font-bold ${influencerPaymentStatusClass(
                row.status
              )}`}
            >
              {influencerPaymentStatusLabel(row.status)}
            </span>
            <div className="mt-1 text-[14px] font-semibold text-[#343a40] px-1 py-1">
              {row.nickname}
            </div>
            {row.paid_at ? (
              <div className="text-[11px] text-[#94a3b8]">
                지급일 {row.paid_at}
              </div>
            ) : null}
          </div>
          <div className="text-right">
            <div className="text-[11px] font-medium text-[#9aa1aa]">
              실 지급액
            </div>
            <div className="text-[15px] font-bold tabular-nums text-[#111827]">
              {formatCurrency(
                parseApiAmount(row.net_amount),
                row.currency ?? "KRW"
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
};

export const CampaignPaymentHistorySection = ({ campaignId }) => {
  const history = useHistory();
  const [expandedPayReportId, setExpandedPayReportId] = useState(null);

  const { data: paymentHistory } = useBrandPaymentHistory({
    campaignId,
    status: null,
    page: 1,
    enabled: Boolean(campaignId),
  });

  const {
    data: creatorPayoutLines,
    isPending: isCreatorPayoutLinesPending,
    isError: isCreatorPayoutLinesError,
  } = useBrandPaymentHistoryDetail({
    payReportId: expandedPayReportId,
    enabled: Boolean(campaignId && expandedPayReportId),
  });

  const payOrders = paymentHistory?.result ?? [];

  const toggleExpand = (orderId, orderStatus) => {
    if (!canOpenSettlementDetail(orderStatus)) {
      return;
    }
    setExpandedPayReportId((prev) => (prev === orderId ? null : orderId));
  };

  if (payOrders.length === 0) {
    return null;
  }

  return (
    <div className="mt-8 border-t border-[#eef2f6] pt-6">
      <div className="mb-4">
        <h3 className="text-[16px] font-bold tracking-[-0.02em] text-[#111827]">
          결제 내역
        </h3>
      </div>

      <ul className="space-y-3">
        {payOrders.map((item) => {
          const currency = item.currency ?? "KRW";
          const amount = parseApiAmount(item.paid_amount);
          const open = expandedPayReportId === item.id;
          const canExpand = canOpenSettlementDetail(item.status);

          return (
            <li
              key={item.id}
              className="overflow-hidden rounded-[20px] border border-[#e7eaee] bg-white shadow-[0_8px_28px_rgba(17,24,39,0.06)]"
            >
              <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <button
                  type="button"
                  disabled={!canExpand}
                  onClick={() => toggleExpand(item.id, item.status)}
                  className="flex min-w-0 flex-1 items-start gap-3 text-left"
                >
                  <span
                    className={`mt-0.5 inline-flex shrink-0 rounded-[8px] px-2 py-0.5 text-[11px] font-bold ${brandPaymentStatusClass(
                      item.status
                    )}`}
                  >
                    {brandPaymentStatusLabel(item.status)}
                  </span>
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-semibold text-[#111827]">
                        {formatCurrency(amount, currency)}
                      </p>
                      <p className="mt-0.5 text-[12px] text-[#94a3b8]">
                        {item.paid_at ? String(item.paid_at) + " · " : null}
                        {item.merchant_uid
                          ? `주문 번호 ${String(item.merchant_uid).slice(4)}`
                          : null}
                      </p>
                    </div>
                    {canExpand ? (
                      <FiChevronDown
                        className={`h-5 w-5 shrink-0 text-[#64748b] transition-transform duration-200 ${
                          open ? "rotate-180" : ""
                        }`}
                        aria-hidden
                      />
                    ) : null}
                  </div>
                </button>
              </div>

              {open && canExpand ? (
                <div className="border-t border-[#f1f5f9] bg-[#fafbfc] px-4 py-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-[12px] font-semibold text-[#64748b]">
                      크리에이터 내역
                    </p>
                    <Button
                      type="default"
                      size="small"
                      className="rounded-lg text-[12px]"
                      onClick={() => {
                        history.push({
                          pathname: `/payments/brand-payments-history/detail/${item.id}`,
                          state: buildPaymentDetailState(item),
                        });
                      }}
                    >
                      전체 상세
                    </Button>
                  </div>
                  <CreatorPayoutList
                    isPending={isCreatorPayoutLinesPending}
                    isError={isCreatorPayoutLinesError}
                    creatorPayoutLines={creatorPayoutLines}
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
